/**
 *  ----------------------------------------------------------------------------
 *  ----------------------------------------------------------------------------
 * This has nothing to do with the conventional Context API.
 * It just contains helper functions for actions such as api calls.
 * If you find a better way to group this functions or methods feel free to
 * refactor and improve the code.
 *
 * Please dont misuse the FREEDOM 😉
 * ----------------------------------------------------------------------------
 * ----------------------------------------------------------------------------
 */

import { descend, groupBy, prop, sortWith } from 'ramda';
import moment from 'moment';
import getColorFromImage from '../utils/getColorFromImage';
import getImageDimensions from '../utils/getImageDimensions';
import { Message, MessageContentType, MessagesQueryType } from '../types/types';
import { getConversationMessages } from '../api/api';

export const MESSAGE_LIMIT = 100;

export const LOAD_MESSAGES_INTERVAL = 4000;

/**
 * Poll interval for the app-shell unanswered-visit indicator, which fans out one
 * `/questionnaires` request per patient with a pending visit. Interim value from
 * PRO-3932 until an aggregate endpoint replaces the fan-out; Layout also
 * refetches on tab focus.
 */
export const LOAD_UNANSWERED_QNAS_INTERVAL = 5 * 60 * 1000;

export async function getMessages({
  page,
  patientId,
  questionnaireId,
  pageSize,
  conversationId,
}: MessagesQueryType) {
  if (!patientId) return [];

  let messages = (await getConversationMessages(questionnaireId, {
    questionnaireId,
    conversationId,
    page,
    pageSize,
  })) as Message[];

  messages = await Promise.all(
    (messages || []).map(async (message: Message) => {
      return {
        ...message,
        isSender: message.userId !== patientId,
        content: message.content,
      };
    }),
  );

  messages = sortWith([descend(prop('createdAt') as any)], messages);

  return messages;
}

export async function loadReceivedMessage({ patientId, questionnaireId }: MessagesQueryType) {
  let messages = await getMessages({ patientId, questionnaireId, pageSize: 3 }); // Fetch 3 for more dataset to test

  messages = (messages || []).filter(message => {
    const today = moment().format('YYYY-MM-DD');
    const msgCreatedAt = moment(message.createdAt).format('YYYY-MM-DD');
    return (
      message.userId === patientId &&
      message.patientQuestionnaireId === questionnaireId &&
      moment(today).isSameOrBefore(msgCreatedAt)
    );
  }) as Message[];

  return messages[0]
    ? {
        ...messages[0],
        isSender: messages[0]?.userId !== patientId,
        content: messages[0]?.content,
      }
    : null;
}

export const getTemporaryMessage = (
  id: string,
  content: string,
  type: MessageContentType,
  userId: string,
  patientQuestionnaireId: string,
) => {
  return {
    id,
    tempMessageId: id,
    content,
    isSender: true,
    type,
    userId,
    patientQuestionnaireId,
    conversationId: '', // TODO
    createdAt: moment(Date.now()).format(),
    updatedAt: moment(Date.now()).format(),
  };
};

export const getImageProperties = async (
  image: string,
): Promise<{ color: string; height: number; width: number }> => {
  let width = image ? (await getImageDimensions(image)).width : 0;

  let height = image ? (await getImageDimensions(image)).height : 0;

  // We want chat images to fit in a container of 300x300 dimension (width of 300px)
  // We only want to possibly manipulate only one of the dimensions, height or width
  const MAX_WIDTH = 300;
  const MAX_HEIGHT = 300;

  if (width > height) {
    if (width > MAX_WIDTH) {
      height *= MAX_WIDTH / width;
      width = MAX_WIDTH;
    }
  } else if (height > MAX_HEIGHT) {
    width *= MAX_HEIGHT / height;
    height = MAX_HEIGHT;
  }

  return {
    color: image ? await getColorFromImage(image) : '',
    height,
    width,
  };
};

export const groupMessagesByDate = groupBy((message: Message) => {
  return moment.utc(message?.createdAt).local().calendar(null, {
    sameDay: '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: 'dddd, DD MMM',
    sameElse: 'DD MMM, YYYY',
  });
});
